import { useRef, useState } from "react";
import { DollarSign, Facebook, Globe, Instagram, Music2, Plus, X } from "lucide-react";
import { type EventLink, type LinkType, detectLinkType, getLinkDisplay } from "@shared/eventLinks";

interface LinkConfig {
  label: string;
  placeholder: string;
  bg: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const LINK_CONFIGS: Record<LinkType, LinkConfig> = {
  web:       { label: "Website",   placeholder: "https://",               bg: "bg-neutral-500",                                Icon: Globe },
  instagram: { label: "Instagram", placeholder: "https://instagram.com/", bg: "bg-gradient-to-br from-purple-500 to-pink-500", Icon: Instagram },
  facebook:  { label: "Facebook",  placeholder: "https://facebook.com/",  bg: "bg-blue-600",                                   Icon: Facebook },
  tiktok:    { label: "TikTok",    placeholder: "https://tiktok.com/@",   bg: "bg-neutral-900",                                Icon: Music2 },
  venmo:     { label: "Venmo",     placeholder: "https://venmo.com/",     bg: "bg-sky-500",                                    Icon: DollarSign },
};

const QUICK_PICKS: LinkType[] = ["instagram", "facebook", "tiktok", "venmo", "web"];

interface Props {
  links: EventLink[];
  onChange: (links: EventLink[]) => void;
}

export function LinksEditor({ links, onChange }: Props) {
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const detectedType = url.trim() ? detectLinkType(url.trim()) : null;
  const detectedConfig = detectedType ? LINK_CONFIGS[detectedType] : null;

  const remove = (i: number) => onChange(links.filter((_, j) => j !== i));

  const commit = () => {
    const raw = url.trim();
    if (!raw) return;
    const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
    const type = detectLinkType(normalized);
    onChange([...links, { type, url: normalized }]);
    setUrl("");
    setAdding(false);
  };

  const pickPlatform = (type: LinkType) => {
    setUrl(LINK_CONFIGS[type].placeholder);
    setAdding(true);
    setTimeout(() => {
      inputRef.current?.focus();
      // Position cursor at end so user types after the pre-filled URL.
      const len = LINK_CONFIGS[type].placeholder.length;
      inputRef.current?.setSelectionRange(len, len);
    }, 50);
  };

  return (
    <div>
      {/* Existing links */}
      {links.length > 0 && (
        <div className="space-y-0.5 mb-3">
          {links.map((link, i) => {
            const lc = LINK_CONFIGS[link.type] ?? LINK_CONFIGS.web;
            const display = getLinkDisplay(link);
            return (
              <div
                key={i}
                className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 group"
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${lc.bg}`}>
                  <lc.Icon className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm font-medium truncate">{display}</span>
                <span className="text-xs text-neutral-400 shrink-0">{lc.label}</span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 shrink-0"
                  aria-label="Remove link"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <div className="space-y-3">
          {/* URL input row */}
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${detectedConfig ? detectedConfig.bg : "bg-neutral-200 dark:bg-neutral-700"}`}
            >
              {detectedConfig
                ? <detectedConfig.Icon className="w-4 h-4 text-white" />
                : <Globe className="w-4 h-4 text-neutral-400" />
              }
            </div>
            <input
              ref={inputRef}
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commit(); }
                if (e.key === "Escape") { setAdding(false); setUrl(""); }
              }}
              placeholder="Paste a URL…"
              className="flex-1 bg-neutral-100 dark:bg-neutral-800 border-0 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500/40 placeholder:text-neutral-400"
            />
            <button
              type="button"
              onClick={commit}
              disabled={!url.trim()}
              className="btn btn-primary btn-sm disabled:opacity-40 shrink-0"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setUrl(""); }}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 shrink-0"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Quick picks */}
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PICKS.map((type) => {
              const lc = LINK_CONFIGS[type];
              const isActive = detectedType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => pickPlatform(type)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors
                    ${isActive
                      ? "border-primary-500 bg-primary-500/10 text-primary-600 dark:text-primary-400"
                      : "border-neutral-200 dark:border-neutral-700 hover:border-primary-400 bg-neutral-50 dark:bg-neutral-800/50 text-neutral-600 dark:text-neutral-400"
                    }`}
                >
                  <div className={`w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 ${lc.bg}`}>
                    <lc.Icon className="w-2 h-2 text-white" />
                  </div>
                  {lc.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 text-sm text-neutral-500 dark:text-neutral-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add link
        </button>
      )}
    </div>
  );
}
